import React from "react";
// ANT DESIGN COMPONENTS
import { Collapse } from "antd";
// REDUX
// TRANSLATION
import { useTranslation } from "react-i18next";
// COMPONENTS
import ConnectionEditor from "./ConnectionEditor";
import SecurityEditor from "./SecurityEditor";
import IdentityEditor from "./IdentityEditor";
import MappingsEditor from "./MappingsEditor";
import SessionEditor from "./SessionEditor";
import RedundancyEditor from "./RedundancyEditor";
import SubscriptionsEditor from "./SubscriptionsEditor";
import MonitorsEditor from "./MonitorsEditor";
import MethodsEditor from "./MethodsEditor";
import EventsEditor from "./EventsEditor";
import HistoryReadsEditor from "./HistoryReadsEditor";
// INTERFACES
import type {
  AdapterCdoOpcUa,
  OpcUaIdentityForm,
  OpcUaCertificateConfigForm,
  OpcUaNodeToMessageRow,
  OpcUaMessageToNodeRow,
  OpcUaSessionForm,
  OpcUaRedundancyForm,
  OpcUaSubscriptionConfigForm,
  OpcUaNamedSubscriptionForm,
  OpcUaNodeMonitorForm,
  OpcUaMethodMappingForm,
  OpcUaEventMonitorForm,
  OpcUaEventToMessageRow,
  OpcUaHistoryReadForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";
// FLATBUFFER ENUMS
import { SecurityPolicy } from "../../../../../flatbuffers/generated/opc-ua/security-policy";
import { MessageSecurityMode } from "../../../../../flatbuffers/generated/opc-ua/message-security-mode";

const { Panel } = Collapse;

/** OPC UA CDO editor: Connection, Security, Identity, Session, Redundancy, Subscriptions, Monitors, Mappings, Methods, Events, and History reads. */
export default function CdoOpcUaPage({
  setCdo,
  cdoValue,
  floors = undefined,
}: {
  setCdo: (newValue: AdapterCdoOpcUa) => void;
  cdoValue: AdapterCdoOpcUa;
  /** The adapter's floor count; caps floor ranges in the mappings editor. */
  floors?: number;
}) {
  const [t] = useTranslation("global");

  const onChangeApplicationName = (value: string) =>
    setCdo({ ...cdoValue, applicationName: value });
  const onChangeSecurityPolicy = (value: SecurityPolicy) =>
    setCdo({ ...cdoValue, securityPolicy: value });
  const onChangeSecurityMode = (value: MessageSecurityMode) =>
    setCdo({ ...cdoValue, securityMode: value });
  const onChangeCertificate = (value: OpcUaCertificateConfigForm | null) =>
    setCdo({ ...cdoValue, certificate: value });
  const onChangeIdentity = (value: OpcUaIdentityForm) => setCdo({ ...cdoValue, identity: value });
  const onChangeSession = (value: OpcUaSessionForm | null) =>
    setCdo({ ...cdoValue, session: value });
  const onChangeRedundancy = (value: OpcUaRedundancyForm | null) =>
    setCdo({ ...cdoValue, redundancy: value });
  const onChangeSubscription = (value: OpcUaSubscriptionConfigForm | null) =>
    setCdo({ ...cdoValue, subscription: value });
  const onChangeNamedSubscriptions = (rows: OpcUaNamedSubscriptionForm[]) =>
    setCdo({ ...cdoValue, namedSubscriptions: rows });
  const onChangeMonitors = (rows: OpcUaNodeMonitorForm[]) =>
    setCdo({ ...cdoValue, monitors: rows });
  const onChangeNodeToMsg = (rows: OpcUaNodeToMessageRow[]) =>
    setCdo({ ...cdoValue, nodeToMsg: rows });
  const onChangeMsgToNode = (rows: OpcUaMessageToNodeRow[]) =>
    setCdo({ ...cdoValue, msgToNode: rows });
  const onChangeMethods = (rows: OpcUaMethodMappingForm[]) =>
    setCdo({ ...cdoValue, methods: rows });
  const onChangeEventMonitors = (rows: OpcUaEventMonitorForm[]) =>
    setCdo({ ...cdoValue, eventMonitors: rows });
  const onChangeEventMappings = (rows: OpcUaEventToMessageRow[]) =>
    setCdo({ ...cdoValue, eventMappings: rows });
  const onChangeHistoryReads = (rows: OpcUaHistoryReadForm[]) =>
    setCdo({ ...cdoValue, historyReads: rows });

  return (
    <div>
      <div>
        <Collapse>
          <Panel header={t("general.opcUaSectionConnection")} key="connection">
            <ConnectionEditor
              applicationName={cdoValue.applicationName}
              onChangeApplicationName={onChangeApplicationName}
            />
          </Panel>
        </Collapse>
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionSecurity")} key="security">
            <SecurityEditor
              securityPolicy={cdoValue.securityPolicy}
              securityMode={cdoValue.securityMode}
              certificate={cdoValue.certificate}
              onChangeSecurityPolicy={onChangeSecurityPolicy}
              onChangeSecurityMode={onChangeSecurityMode}
              onChangeCertificate={onChangeCertificate}
            />
          </Panel>
        </Collapse>
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionIdentity")} key="identity">
            <IdentityEditor identity={cdoValue.identity} onChangeIdentity={onChangeIdentity} />
          </Panel>
        </Collapse>
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionSession")} key="session">
            <SessionEditor session={cdoValue.session} onChangeSession={onChangeSession} />
          </Panel>
        </Collapse>
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionRedundancy")} key="redundancy">
            <RedundancyEditor
              redundancy={cdoValue.redundancy}
              onChangeRedundancy={onChangeRedundancy}
            />
          </Panel>
        </Collapse>
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionSubscriptions")} key="subscriptions">
            <SubscriptionsEditor
              subscription={cdoValue.subscription}
              onChangeSubscription={onChangeSubscription}
              namedSubscriptions={cdoValue.namedSubscriptions}
              onChangeNamedSubscriptions={onChangeNamedSubscriptions}
            />
          </Panel>
        </Collapse>
        <br />
        {/* MonitorsEditor already renders its own Collapse with a dynamic
            "Node Monitors (N)" header — an outer generic "Monitors" panel
            wrapping it just doubled the click-to-expand with no extra
            information. Rendered directly, exactly like MappingsEditor
            below (which has the same shape: its own named panel(s), no
            outer wrapper), so the two no longer look structurally
            different from each other. */}
        <MonitorsEditor
          monitors={cdoValue.monitors}
          onChangeMonitors={onChangeMonitors}
          namedSubscriptions={cdoValue.namedSubscriptions}
          hasDefaultSubscription={cdoValue.subscription !== null}
        />
        <br />
        <MappingsEditor
          monitors={cdoValue.monitors}
          floors={floors}
          nodeToMsg={cdoValue.nodeToMsg}
          msgToNode={cdoValue.msgToNode}
          onChangeNodeToMsg={onChangeNodeToMsg}
          onChangeMsgToNode={onChangeMsgToNode}
        />
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionMethods")} key="methods">
            <MethodsEditor methods={cdoValue.methods} onChangeMethods={onChangeMethods} />
          </Panel>
        </Collapse>
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionEvents")} key="events">
            <EventsEditor
              eventMonitors={cdoValue.eventMonitors}
              onChangeEventMonitors={onChangeEventMonitors}
              eventMappings={cdoValue.eventMappings}
              onChangeEventMappings={onChangeEventMappings}
            />
          </Panel>
        </Collapse>
        <br />
        <Collapse>
          <Panel header={t("general.opcUaSectionHistoryReads")} key="history-reads">
            <HistoryReadsEditor
              historyReads={cdoValue.historyReads}
              onChangeHistoryReads={onChangeHistoryReads}
            />
          </Panel>
        </Collapse>
      </div>
    </div>
  );
}
